import classNames from 'classnames'
import { FunctionComponent, useEffect, useState } from 'react'
import {
  Brand,
  Creator,
  MessageThread,
  ThreadStatus,
  BrandServiceLevel,
} from '../../../../gql/types'
import { Avatar } from '../../../../elements'
import {
  Button,
  ButtonColor,
  ButtonSize,
  ButtonStyle,
  Icon,
  IconType,
  NotificationType,
} from '../../../../components'
import {
  createSearchParams,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { ViewCollaborationsSlider } from '../../../../components/ViewCollaborationsSlider'
import { ViewAllClipsSlider } from '../../../../components/ViewAllClipsSlider'
import { BrandRoutes } from '../../../BrandRoutes'
import { useNotification } from '../../../../utils/hooks'
import { useMutation } from '@apollo/client'
import {
  MARK_THREAD_MESSAGES_AS_UNREAD_BY_BRAND,
  ARCHIVE_THREAD,
  ACTIVATE_THREAD,
} from '../../../../gql/messages'
import { PricingData } from '../../../../utils/PricingUtil'
import { ViewCreatorSlider } from '../../../shared/ViewCreatorSlider'
import { Tooltip } from 'react-tooltip'
import { TipModal } from '../../../shared/TipModal'

interface IProps {
  className?: string
  creator?: Creator
  brand?: Brand
  pricing: PricingData
  activeThread: MessageThread
  isDefaultTipModalOpen?: boolean
  onTipModalClose?: () => void
}

const Header: FunctionComponent<IProps> = ({
  className,
  creator,
  brand,
  pricing,
  activeThread,
  isDefaultTipModalOpen,
  onTipModalClose,
}) => {
  const { addNotification } = useNotification()
  const navigate = useNavigate()
  const { id, uuid } = useParams()

  // in order to get campaign
  const [searchParams] = useSearchParams()
  const [activeCollaborationId, setActiveCollaborationId] = useState(null)
  const [isTipModalOpen, setIsTipModalOpen] = useState(isDefaultTipModalOpen)

  useEffect(() => {
    if (searchParams.get('collaborationId')) {
      setActiveCollaborationId(parseInt(searchParams.get('collaborationId')))
    }
  }, [searchParams])

  const [isViewCollaborationsSliderOpen, setIsViewCollaborationsSliderOpen] =
    useState(false)

  const [isViewClipsSliderOpen, setIsViewClipsSliderOpen] = useState(false)
  const [isViewProfileSliderOpen, setIsViewProfileSliderOpen] = useState(false)

  const [markThreadMessagesAsUnreadByBrand] = useMutation(
    MARK_THREAD_MESSAGES_AS_UNREAD_BY_BRAND,
  )
  const [archiveThread] = useMutation(ARCHIVE_THREAD)
  const [activateThread] = useMutation(ACTIVATE_THREAD)
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const mobileMediaQuery = window.matchMedia('(max-width: 767px)') // Adjust the breakpoint as needed

    const handleMobileChange = (event) => {
      setIsMobile(event.matches)
    }

    mobileMediaQuery.addEventListener('change', handleMobileChange)
    setIsMobile(mobileMediaQuery.matches)

    return () => {
      mobileMediaQuery.removeEventListener('change', handleMobileChange)
    }
  }, [])

  const onMarkConversationUnread = async () => {
    try {
      await markThreadMessagesAsUnreadByBrand({
        variables: {
          uuid,
        },
      })

      addNotification(`Conversation marked as unread`, NotificationType.success)
      navigate({
        pathname: BrandRoutes.inbox.replace(':id', `${id}`),
        search: createSearchParams(searchParams).toString(),
      })
    } catch (error) {
      addNotification(
        `We couldn't mark this conversation as unread.`,
        NotificationType.error,
      )
    }
  }

  const onArchiveThread = async () => {
    try {
      await archiveThread({
        variables: {
          uuid,
        },
      })

      addNotification(`Thread archived successfully`, NotificationType.success)
      // navigate({
      //   pathname: BrandRoutes.inbox.replace(':id', `${id}`),
      //   search: createSearchParams(searchParams).toString(),
      // })
    } catch (error) {
      addNotification(
        `We couldn't archive this thread.`,
        NotificationType.error,
      )
    }
  }

  const onActivateThread = async () => {
    try {
      await activateThread({
        variables: {
          uuid,
        },
      })

      addNotification(`Thread activated successfully`, NotificationType.success)
    } catch (error) {
      addNotification(
        `We couldn't activate this thread.`,
        NotificationType.error,
      )
    }
  }

  if (!creator || !brand) {
    return null
  }

  return (
    <>
      <div
        className={classNames(
          'md:flex md:items-center md:justify-between md:space-x-5',
          className,
        )}
      >
        <div
          className='flex items-center space-x-5 align-middle justify-center cursor-pointer'
          onClick={() => setIsViewProfileSliderOpen(true)}
        >
          <div className='flex-shrink-0'>
            <div className='relative'>
              <Avatar
                size='md'
                src={creator?.avatar?.url}
                type='user'
                alt='Logo'
              />
            </div>
          </div>

          <div className='max-w-sm overflow-hidden'>
            <h1 className='text-xl font-bold text-gray-900'>
              {creator.firstName}
            </h1>
          </div>
        </div>

        <div className='mt-6 flex flex-row space-x-1 justify-stretch space-y-4 space-y-reverse sm:justify-end sm:space-x-reverse sm:space-y-0 sm:space-x-1 md:mt-0 md:flex-row md:space-x-1'>
          {/* Archive/Activate Thread button - only for enterprise and white glove brands */}
          {(brand.serviceLevel === BrandServiceLevel.enterprise ||
            brand.serviceLevel === BrandServiceLevel.whiteGlove) && (
            <Button
              icon={
                <Icon
                  type={
                    activeThread.status === ThreadStatus.active
                      ? IconType.eyeOff
                      : IconType.eye
                  }
                  className='h-4 w-4 text-white'
                  aria-hidden='true'
                />
              }
              iconPosition='left'
              colour={ButtonColor.slate}
              styleType={ButtonStyle.fill}
              onClick={
                activeThread.status === ThreadStatus.active
                  ? onArchiveThread
                  : onActivateThread
              }
              className='w-full h-full'
              tooltip={
                activeThread.status === ThreadStatus.active
                  ? 'Archive thread'
                  : 'Activate thread'
              }
            />
          )}
          <Button
            icon={
              <Icon
                type={IconType.removeDoneAll}
                className='h-4 w-4'
                aria-hidden='true'
              />
            }
            iconPosition='left'
            colour={ButtonColor.white}
            onClick={onMarkConversationUnread}
            className='w-full h-full'
            tooltip='Mark message as unread'
          />
          <Button
            icon={
              <Icon
                type={IconType.money}
                className='h-4 w-4'
                aria-hidden='true'
              />
            }
            iconPosition='left'
            colour={ButtonColor.tertiary}
            onClick={() => setIsTipModalOpen(true)}
            className='w-full h-full'
            tooltip='Reward the creator'
          />
          <Button
            type='button'
            colour={ButtonColor.secondary}
            onClick={() => setIsViewClipsSliderOpen(true)}
            size={ButtonSize.medium}
            icon={
              <Icon
                type={IconType.filmThree}
                className='h-4 w-4 text-white'
                aria-hidden='true'
              />
            }
            iconPosition='left'
            className='w-full h-full'
            tooltip='View clips'
          />
          <Button
            // title={isMobile ? 'Campaigns' : null}
            type='button'
            colour={ButtonColor.primary}
            onClick={() => setIsViewCollaborationsSliderOpen(true)}
            size={ButtonSize.medium}
            icon={
              <div className='relative'>
                <Icon
                  type={IconType.layers}
                  className='h-4 w-4 text-white'
                  aria-hidden='true'
                />
              </div>
            }
            iconPosition='left'
            className='w-full h-full'
            tooltip='View campaigns'
          />
          {/* <Button
            // title='Profile'
            type='button'
            colour={ButtonColor.lightGray}
            onClick={() => setIsViewProfileSliderOpen(true)}
            size={ButtonSize.small}
            icon={
                <Icon
                  type={IconType.user}
                  className='h-4 w-4'
                  aria-hidden='true'
                />
            }
            iconPosition='left'
            className='w-full h-full'
            tooltip='View profile'
          /> */}
        </div>
      </div>

      {creator && brand && (
        <>
          <ViewCollaborationsSlider
            open={isViewCollaborationsSliderOpen}
            setOpen={setIsViewCollaborationsSliderOpen}
            creator={creator}
            brand={brand}
            type='brand'
          />
          <ViewAllClipsSlider
            open={isViewClipsSliderOpen}
            setOpen={setIsViewClipsSliderOpen}
            creator={creator}
            brand={brand}
            type='brand'
          />
          <ViewCreatorSlider
            open={isViewProfileSliderOpen}
            setOpen={setIsViewProfileSliderOpen}
            creator={creator}
          />

          <TipModal
            brand={brand}
            creator={creator}
            open={isTipModalOpen}
            setOpen={() => {
              if (isTipModalOpen) {
                setIsTipModalOpen(false)
                onTipModalClose()
              }

              if (!isTipModalOpen) {
                setIsTipModalOpen(true)
              }
            }}
            threadId={activeThread?.id}
          />
        </>
      )}
    </>
  )
}

export { Header }
